import {useEffect, useRef} from 'react';
import {requestContext} from '../../requestContext';

const SCROLL_THRESHOLD_PX = 100;

/**
 * iOS에서 최하단에서 스크롤을 할 경우 elastic scroll이 발생하여 스크롤이 아래로 내려갔다가 다시 위로 튕기는 현상이 발생합니다.
 * sticky + overflow 조합에서 해당 현상이 발생하면 overflow 클리핑을 재계산하는데 이때 해당 영역이 미노출되는 이슈가 발생합니다.
 * 이를 해결하기 위해 최하단에서 스크롤이 100px 이내로 내려왔을 때 body의 overscroll-behavior-y를 none으로 전환하여 elastic scroll를 막습니다.
 * 상단에서는 pull to refresh 기능을 사용해야하기 때문에 다시 초기화힙니다.
 */
export function useBodyOverscrollLock() {
  const {isIos, isWebView} = requestContext.requestInfo.display;
  const isLockedRef = useRef(false);

  useEffect(() => {
    // 웹뷰에선 overflow로 인한 이슈가 발생하는 화면이 없고 iOS 네이티브 화면에서는 elastic scroll이 발생하기 때문에 통일된 UX를 위해서 웹뷰에서는 스크롤을 막지 않습니다.
    if (
      !isIos ||
      isWebView ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    const lockBody = () => {
      if (isLockedRef.current) {
        return;
      }
      document.documentElement.style.overscrollBehaviorY = 'none';
      isLockedRef.current = true;
    };

    const unlockBody = () => {
      if (!isLockedRef.current) {
        return;
      }

      document.documentElement.style.overscrollBehaviorY = '';
      isLockedRef.current = false;
    };

    const handleScroll = () => {
      const distanceFromBottom =
        document.documentElement?.scrollHeight -
        window.innerHeight -
        window.scrollY;

      if (distanceFromBottom <= SCROLL_THRESHOLD_PX) {
        lockBody();
        return;
      }

      unlockBody();
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      unlockBody();
    };
  }, [isIos, isWebView]);
}
